import { v4 } from "uuid";

import {
  CuratedPlaceOfSafetySchema,
  dayjs,
  pickRandom,
  SAMPLE_FORENAMES,
  SAMPLE_SURNAMES,
  type CuratedPlaceOfSafety,
  type CuratedPlaceOfSafetyUnbranded,
  type Dayjs,
} from "@thalamos/common";

import { LocationSchema, type LocationUnbranded } from "../types/location.js";
import {
  WorkflowVersionSchema,
  type WorkflowVersion,
} from "../types/workflowVersion.js";

type SectionData = Record<string, unknown>;

export type PersonName = { forename: string; surname: string };

export type QuickCreateSampleData = {
  createIncidentMain: SectionData;
  triageMain: SectionData;
  personDetails: SectionData;
  detentionDetails: SectionData;
  informationForHealthcare: SectionData;
  recordPlaceOfSafetyMain: SectionData;
  recordPlaceOfSafetyArrival: SectionData;
};

/**
 * Optional context for generating quick-create sample data.
 *
 * - `now` — anchor for all relative date/times (defaults to current time)
 * - `curatedPlaceOfSafety` — if provided, uses this curated POS instead of
 *   a fabricated ad-hoc one, making the created incident appear more realistic.
 * - `name` — if provided, names the person instead of picking a random name.
 *   The same name is used everywhere the person is recorded.
 */
export type QuickCreateSampleDataOptions = {
  now?: Dayjs;
  curatedPlaceOfSafety?: CuratedPlaceOfSafety;
  name?: PersonName;
};

/**
 * A dispatch number in the shape forces actually use: bare digits, no prefix.
 * Short enough to collide, which quick-created incidents tolerate.
 */
export function generateDemoDispatchNumber(): string {
  const digits = 4 + Math.floor(Math.random() * 3);
  const smallest = 10 ** (digits - 1);
  return String(smallest + Math.floor(Math.random() * smallest * 9));
}

// -- Shared building blocks --

function buildSharedCreateIncidentBase(name: PersonName): SectionData {
  return {
    // Fixed rather than generated, so this sample data stays deterministic.
    dispatchNumber: { value: "123456" },
    commandUnit: { value: v4(), valueLabel: "Test Command Unit" },
    personForename: { value: name.forename, isUnknown: false },
    personSurname: { value: name.surname, isUnknown: false },
  };
}

function buildSharedPersonDetails(name: PersonName): SectionData {
  return {
    personForename: { value: name.forename, isUnknown: false },
    personSurname: { value: name.surname, isUnknown: false },
    dateOfBirth: { value: "1990-01-01", isUnknown: false },
    satisfiedPersonIsOver18: null,
    personAddress: {
      value: LocationSchema.parse({
        primaryLocationType: "postalAddress",
        userProvided: { formattedValue: "123 High St" },
        postalAddress: [
          {
            shortName: "123 High St",
            longName: "123 High Street",
            types: ["str"],
          },
        ],
      } satisfies LocationUnbranded),
      isUnknown: false,
    },
    confirmPersonAddress: { value: "yes", valueLabel: "Yes" },
    gender: { value: "preferNotToSay", valueLabel: "Prefer not to say" },
    ethnicAppearance: { value: "IC1", valueLabel: "White" },
    selfDeclaredEthnicity: {
      value: "whiteEnglish",
      valueLabel:
        "White - English / Welsh / Scottish / Northern Irish / British",
    },
    selfDeclaredEthnicityOther: null,
    hasRelativeOrFriend: { value: "no", valueLabel: "No" },
    relativeOrFriendForename: null,
    relativeOrFriendSurname: null,
    relativeOrFriendRelationship: null,
    relativeOrFriendSameAddress: null,
    relativeOrFriendAddress: null,
    relativeOrFriendTelephoneNumber: null,
    relativeOrFriendInformed: null,
    reasonNotInformed: null,
  };
}

function buildSharedDetentionDetailsBase(now: Dayjs): SectionData {
  return {
    // A number recorded against a detention held anywhere but custody keeps the
    // custody question on screen for one that never was, so a scenario held in
    // custody sets its own rather than inheriting one from here.
    custodyNumber: null,
    dateTimeOfDetention: {
      value: now.subtract(2, "hour").toISOString(),
    },
    locationDetentionTookPlace: {
      value: LocationSchema.parse({
        primaryLocationType: "userProvided",
        userProvided: { formattedValue: "High Street" },
      }),
    },
    orgResponsibleForDetention: {
      value: v4(),
      valueLabel: "Test Organisation",
    },
  };
}

const sharedInformationForHealthcare: SectionData = {
  incidentDescription: { value: "Incident description" },
  wasHealthcareProvided: { value: "no", valueLabel: "No" },
  medicalAttentionProvided: null,
  wasForceUsed: { value: "no", valueLabel: "No" },
  useOfForceType: null,
  useOfForceContextHandcuffing: null,
  useOfForceContextHandcuffingOther: null,
  useOfForceContextUnarmedSkills: null,
  useOfForceContextUnarmedSkillsOther: null,
  useOfForceContextDogs: null,
  useOfForceContextDogsOther: null,
  useOfForceContextBaton: null,
  useOfForceContextBatonOther: null,
  useOfForceContextIrritantSpray: null,
  useOfForceContextIrritantSprayOther: null,
  useOfForceContextLimbBodyRestraints: null,
  useOfForceContextLimbBodyRestraintsOther: null,
  useOfForceContextSpitGuard: null,
  useOfForceContextSpitGuardOther: null,
  useOfForceContextShield: null,
  useOfForceContextShieldOther: null,
  useOfForceContextCed: null,
  useOfForceContextCedOther: null,
  useOfForceContextAep: null,
  useOfForceContextAepOther: null,
  useOfForceContextFirearms: null,
  useOfForceContextFirearmsOther: null,
  useOfForceContextOtherImprovised: null,
  useOfForceContextOtherImprovisedOther: null,
  hasPersonBeenSearched: { value: "no", valueLabel: "No" },
  didSeizeProperty: null,
  detailsPropertySeized: null,
  hasAdditionalRiskFactors: { value: "no", valueLabel: "No" },
  describeRiskFactors: null,
  describeRiskFactorsOther: null,
};

const posTypeLabels: Record<string, string> = {
  hbPos136: "HBPOS (136 Suite)",
  emergencyDepartment: "Emergency Department (A&E)",
  returnedToMentalHealthUnit: "Returned to Mental Health Unit",
  careHome: "Care Home",
  custody: "Custody (exceptional circumstances)",
  privateHome: "Private Home",
  other: "Other",
};

function buildPlaceOfSafetyMain(
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
): SectionData {
  // Always use "hbPos136" as the placeOfSafetyType regardless of the curated
  // POS's actual posType. Choosing a different type (e.g. "careHome", "custody")
  // would make conditional fields like whyNotHbPosReason or
  // whoAuthorisedCustodySuite visible and required, which the quick-create
  // sample data does not populate.
  if (curatedPlaceOfSafety) {
    return {
      placeOfSafety: curatedPlaceOfSafety,
      confirmPlaceOfSafetyCorrect: { value: "yes", valueLabel: "Yes" },
      placeOfSafetyType: {
        value: "hbPos136",
        valueLabel: posTypeLabels["hbPos136"],
      },
    };
  }

  // Fallback: fabricate a curated POS with synthetic data
  return {
    placeOfSafety: CuratedPlaceOfSafetySchema.parse({
      id: v4(),
      name: "Place of Safety",
      address: "Place of Safety Address",
      postalCode: "AB12 3CD",
      odsCode: "ODS1",
      organisationId: v4(),
      posType: "hbPos136",
    } satisfies CuratedPlaceOfSafetyUnbranded),
    confirmPlaceOfSafetyCorrect: { value: "yes", valueLabel: "Yes" },
    placeOfSafetyType: {
      value: "hbPos136",
      valueLabel: "HBPOS (136 Suite)",
    },
  };
}

function buildRecordPlaceOfSafetyArrival(now: Dayjs): SectionData {
  return {
    arrivalDateTime: {
      value: now.subtract(30, "minute").toISOString(),
    },
    didNotArrive: null,
    wasEntryDelayed: { value: "no", valueLabel: "No" },
    whyWasEntryDelayed: null,
    whyWasEntryDelayedOther: null,
    howLongWasEntryDelayed: null,
    methodOfTransport: {
      value: "policeVehicle",
      valueLabel: "Police vehicle",
    },
    whyMethodOfTransportPoliceVehicle: {
      value: "ambulanceAvailableInTimeframe",
      valueLabel: "Ambulance not available within agreed timeframe",
    },
    whyMethodOfTransportPoliceVehicleOther: null,
    methodOfTransportOther: null,
  };
}

function buildExtendedTriageMain(reasonNotConsulted: {
  value: string;
  valueLabel: string;
}): SectionData {
  return {
    isHealthcareProfessionalConsulted: { value: "no", valueLabel: "No" },
    howDidYouContactNhs: null,
    howDidYouContactNhsOtherDetails: null,
    reasonNotConsulted,
    givenHealthCareProfessionalAdvice: null,
    reasonsForDetention: { value: "Reasons for detention" },
    voluntaryAction: null,
    voluntaryActionOtherDetails: null,
    whatWillYouDo: { value: "136", valueLabel: "136" },
    whatWillYouDoOther: null,
  };
}

// -- met-1.0 --

function buildMetData(
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
): QuickCreateSampleData {
  const posMain = buildPlaceOfSafetyMain(curatedPlaceOfSafety);
  return {
    createIncidentMain: {
      ...buildSharedCreateIncidentBase(name),
      dispatchNumberCreated: {
        value: now.subtract(2, "hour").subtract(30, "minute").toISOString(),
      },
    },
    triageMain: {
      ...buildExtendedTriageMain({
        value: "immediateRestraint",
        valueLabel:
          "There was an immediate need to restrain the person to protect them or others",
      }),
      clinicalCaseRecordNumber: null,
      confirmMentalCapacityLack: null,
      otherAction: null,
      otherActionDetails: null,
    },
    personDetails: {
      ...buildSharedPersonDetails(name),
      personLocalAuthoirityAddress: {
        value: null,
        isUnknown: true,
      },
      completedPoliceNationalComputerCheck: {
        value: "no",
        valueLabel: "No",
      },
      pncRecordForPerson: null,
      pncidNumber: null,
      pncWarningMarkers: null,
    },
    detentionDetails: {
      ...buildSharedDetentionDetailsBase(now),
      userIsAttendingOfficer: { value: "yes", valueLabel: "Yes" },
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: null,
      confirmPersonAddress: null,
      londonBoroughDetentionTookPlace: {
        value: "islington",
        valueLabel: "Islington",
      },
      locationType: { value: "street", valueLabel: "Street" },
      confirmCustodySuite: null,
      incidentAndLocationSameLocation: { value: "yes", valueLabel: "Yes" },
      incidentLocation: null,
      commandUnit: null,
      londonBoroughIncidentTookPlace: null,
      locationTypeIncident: null,
    },
    informationForHealthcare: {
      ...sharedInformationForHealthcare,
      describeForceUsed: null,
      isUnderInfluence: { value: "no", valueLabel: "No" },
    },
    recordPlaceOfSafetyMain: {
      ...posMain,
      whyTakingPersonToEmergencyDepartment: null,
      otherEmergencyDepartmentReasonDetails: null,
    },
    recordPlaceOfSafetyArrival: buildRecordPlaceOfSafetyArrival(now),
  };
}

// -- btp (all versions) --

function buildBtpData(
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
): QuickCreateSampleData {
  const posMain = buildPlaceOfSafetyMain(curatedPlaceOfSafety);
  return {
    createIncidentMain: {
      ...buildSharedCreateIncidentBase(name),
      dispatchNumberCreated: { value: now.format("YYYY-MM-DD") },
      geographicUnit: { value: v4(), valueLabel: "Test Geographic Unit" },
      userIsAttendingOfficer: { value: "yes", valueLabel: "Yes" },
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: null,
    },
    triageMain: {
      ...buildExtendedTriageMain({
        value: "violent",
        valueLabel: "Patient is violent",
      }),
      explainWhyActionDiffers: null,
    },
    personDetails: {
      ...buildSharedPersonDetails(name),
      personLocalAuthoirityAddress: {
        value: null,
        isUnknown: true,
      },
      completedPoliceNationalComputerCheck: {
        value: "no",
        valueLabel: "No",
      },
      pncRecordForPerson: null,
      pncidNumber: null,
      pncWarningMarkers: null,
    },
    detentionDetails: {
      ...buildSharedDetentionDetailsBase(now),
      locationType: { value: "concourse", valueLabel: "Concourse" },
      locationTypeOther: null,
      geographicUnit: null,
      incidentAndLocationSameLocation: { value: "yes", valueLabel: "Yes" },
    },
    informationForHealthcare: {
      ...sharedInformationForHealthcare,
      describeForceUsed: null,
    },
    recordPlaceOfSafetyMain: {
      ...posMain,
      whyTakingPersonToEmergencyDepartment: null,
      otherEmergencyDepartmentReasonDetails: null,
    },
    recordPlaceOfSafetyArrival: buildRecordPlaceOfSafetyArrival(now),
  };
}

// -- colp-1.0 --

function buildColpData(
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
): QuickCreateSampleData {
  const posMain = buildPlaceOfSafetyMain(curatedPlaceOfSafety);
  return {
    createIncidentMain: {
      ...buildSharedCreateIncidentBase(name),
      dispatchNumberCreated: {
        value: now.subtract(2, "hour").subtract(30, "minute").toISOString(),
      },
    },
    triageMain: {
      ...buildExtendedTriageMain({
        value: "immediateRestraint",
        valueLabel:
          "There was an immediate need to restrain the person to protect them or others",
      }),
      clinicalCaseRecordNumber: null,
      confirmMentalCapacityLack: null,
      otherAction: null,
      otherActionDetails: null,
    },
    personDetails: {
      ...buildSharedPersonDetails(name),
      personLocalAuthoirityAddress: {
        value: null,
        isUnknown: true,
      },
      completedPoliceNationalComputerCheck: {
        value: "no",
        valueLabel: "No",
      },
      pncRecordForPerson: null,
      pncidNumber: null,
      pncWarningMarkers: null,
    },
    detentionDetails: {
      ...buildSharedDetentionDetailsBase(now),
      userIsAttendingOfficer: { value: "yes", valueLabel: "Yes" },
      createdOnBehalfOfAnotherOfficerWithShoulderNumber: null,
      confirmPersonAddress: null,
      londonBoroughDetentionTookPlace: {
        value: "islington",
        valueLabel: "Islington",
      },
      locationType: { value: "street", valueLabel: "Street" },
      confirmCustodySuite: null,
      incidentAndLocationSameLocation: { value: "yes", valueLabel: "Yes" },
      incidentLocation: null,
      commandUnit: null,
      londonBoroughIncidentTookPlace: null,
      locationTypeIncident: null,
    },
    informationForHealthcare: {
      ...sharedInformationForHealthcare,
      describeForceUsed: null,
      isUnderInfluence: { value: "no", valueLabel: "No" },
    },
    recordPlaceOfSafetyMain: {
      ...posMain,
      whyTakingPersonToEmergencyDepartment: null,
      otherEmergencyDepartmentReasonDetails: null,
    },
    recordPlaceOfSafetyArrival: buildRecordPlaceOfSafetyArrival(now),
  };
}

// -- Lookup --

type WorkflowDataBuilder = (
  now: Dayjs,
  curatedPlaceOfSafety: CuratedPlaceOfSafety | undefined,
  name: PersonName,
) => QuickCreateSampleData;

const builderByWorkflow: Record<WorkflowVersion, WorkflowDataBuilder> = {
  [WorkflowVersionSchema.enum.MET_1_0]: buildMetData,
  [WorkflowVersionSchema.enum.BTP_1_0]: buildBtpData,
  [WorkflowVersionSchema.enum.BTP_1_1]: buildBtpData,
  [WorkflowVersionSchema.enum.BTP_1_2]: buildBtpData,
  [WorkflowVersionSchema.enum.COLP_1_0]: buildColpData,
};

export function getQuickCreateSampleData(
  workflowVersion: string,
  options: QuickCreateSampleDataOptions = {},
): QuickCreateSampleData {
  const now = options.now ?? dayjs.utc();
  const name: PersonName = options.name ?? {
    forename: pickRandom(SAMPLE_FORENAMES),
    surname: pickRandom(SAMPLE_SURNAMES),
  };
  const builder =
    builderByWorkflow[workflowVersion as WorkflowVersion] ?? buildMetData;
  return builder(now, options.curatedPlaceOfSafety, name);
}
